/**
 * Scroll to the block a dashboard search result points at (COL-623).
 *
 * The target is a `data-search-target` attribute, not a ref: the blocks live
 * in four different screens, and the search box should not have to reach
 * into each one. The element may not exist on the first frame — the tab has
 * only just been switched — so it is looked for a few times before giving up.
 *
 * Finding it is not enough: the blocks above it (payments, the contract)
 * load their own data and grow after the first paint. Scrolling then lands
 * short — in UI QA the landlord confirmation ended half below the fold — so
 * it waits until the block has stopped moving before it scrolls.
 *
 * `onDone` runs once the target is handled (found or not), so the caller can
 * drop the URL parameter and a refresh or Back does not scroll again.
 */

import { useEffect, useRef } from "react";

import type { SearchTarget } from "@/lib/tenant-dashboard-search";

const POLL_MS = 100;
const MAX_TRIES = 30;
/** Polls in a row at the same document position that count as settled. */
const STABLE_TICKS = 3;
const HIGHLIGHT_MS = 1800;

export function useScrollToSearchTarget(
  target: SearchTarget | null,
  onDone: () => void
) {
  // Held in a ref so an inline callback does not restart the search.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!target) return;
    let tries = 0;
    let lastTop: number | null = null;
    let stable = 0;
    const timer = window.setInterval(() => {
      const el = document.querySelector<HTMLElement>(
        `[data-search-target="${target}"]`
      );
      tries += 1;
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY;
        stable = top === lastTop ? stable + 1 : 0;
        lastTop = top;
      }
      // Past the cap, scroll to wherever it is (or give up if it never came).
      if (tries < MAX_TRIES && (!el || stable < STABLE_TICKS)) return;
      window.clearInterval(timer);
      if (el) {
        const reduceMotion = window.matchMedia?.(
          "(prefers-reduced-motion: reduce)"
        ).matches;
        el.scrollIntoView({
          behavior: reduceMotion ? "auto" : "smooth",
          block: "center",
        });
        // A short ring so the eye finds the block it landed on. `animate`
        // leaves no class behind, and is missing in jsdom.
        el.animate?.(
          [
            { boxShadow: "0 0 0 3px hsl(var(--ring) / 0.6)" },
            { boxShadow: "0 0 0 3px hsl(var(--ring) / 0)" },
          ],
          { duration: HIGHLIGHT_MS, easing: "ease-out" }
        );
      }
      onDoneRef.current();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [target]);
}
